/** Turkish formatting helpers — orders module. */

const qtyFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })
const amountFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Korgun currency code → symbol. P_DOV_Tip: EU=EURO, TL=Turkish Lira, US=Dollar. */
const CURRENCY_SYMBOL: Record<string, string> = {
  TL: '₺',
  EU: '€',
  US: '$',
}

export function currencySymbol(code: string | null): string {
  if (!code) return ''
  return CURRENCY_SYMBOL[code] ?? code
}

export function formatQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return qtyFormat.format(value)
}

export function formatAmount(value: number | null | undefined, code: string | null = null): string {
  if (value === null || value === undefined) return '—'
  const symbol = currencySymbol(code)
  return symbol ? `${amountFormat.format(value)} ${symbol}` : amountFormat.format(value)
}

/** API dates are 'YYYY-MM-DD' text; converted to dd.mm.yyyy without going through Date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return iso
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD HH:MM' → 'dd.mm.yyyy HH:MM'. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const [datePart, time] = value.split(' ')
  const d = formatDate(datePart ?? null)
  return time ? `${d} ${time}` : d
}

/**
 * Today by factory clock (Europe/Istanbul) — 'YYYY-MM-DD'.
 *
 * The browser's own timezone can't be trusted: viewed from abroad, or from a
 * machine with the wrong clock, the filter would drift by a day. The `en-CA`
 * locale conveniently already produces YYYY-MM-DD.
 */
const istanbulDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function todayIstanbul(): string {
  return istanbulDayFormat.format(new Date())
}

/** `days` days before today by factory clock — 'YYYY-MM-DD', for date-range presets. */
export function daysAgo(days: number): string {
  const d = new Date(`${todayIstanbul()}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

export function percentText(value: number): string {
  return `%${qtyFormat.format(value)}`
}
